// Iter-193u — Dashboard widget: NDF + Cabinet category breakdown.
// Shows the user's own expenses by category, plus a CEO/GESTIONNAIRE-only
// toggle to switch to the global (all collaborators) view. Cabinet stats
// are only available to CEO/GESTIONNAIRE.
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ScanLine, Wallet, Loader2 } from "lucide-react";

const fmt = (n) =>
  n == null
    ? "—"
    : Number(n).toLocaleString("fr-MA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PERIODS = [
  { value: "month", label: "Ce mois" },
  { value: "ytd", label: "YTD" },
  { value: "6m", label: "6 mois" },
  { value: "12m", label: "12 mois" },
];

// Stable color palette for category bars.
const CAT_COLORS = [
  "bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-red-500",
  "bg-violet-500", "bg-cyan-500", "bg-pink-500", "bg-orange-500",
  "bg-lime-500", "bg-teal-500", "bg-fuchsia-500", "bg-rose-500",
];

function CategoryBars({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="text-xs text-zinc-400 italic py-4">Aucune donnée</div>;
  }
  const max = rows[0]?.total_mad || 1;
  return (
    <div className="space-y-1.5" data-testid="exp-stats-bars">
      {rows.map((r, idx) => (
        <div key={r.category} className="flex items-center gap-2 text-xs" data-testid={`exp-stats-row-${r.category}`}>
          <div className="w-24 truncate font-mono text-zinc-700" title={r.category}>{r.category}</div>
          <div className="flex-1 bg-zinc-100 rounded h-4 overflow-hidden relative">
            <div
              className={`h-full ${CAT_COLORS[idx % CAT_COLORS.length]} transition-all`}
              style={{ width: `${Math.max(2, (r.total_mad / max) * 100)}%` }}
            />
            <span className="absolute inset-0 flex items-center justify-end pr-2 text-[10px] font-mono text-zinc-800">
              {fmt(r.total_mad)} <span className="text-zinc-500 ml-0.5">({r.count})</span>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ExpenseStatsBlock({ user }) {
  const [period, setPeriod] = useState("month");
  const [scope, setScope] = useState("me");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Iter-228 — Gate the widget behind /expenses/feature-status so a locked
  // COLLABORATOR (or any user who hasn't been unlocked by the CEO) doesn't
  // trigger a 403 on /expenses/stats/dashboard at dashboard load.
  const [unlocked, setUnlocked] = useState(null); // null=unknown, true/false=resolved
  const isAdminReader = user && (user.role === "CEO" || user.role === "GESTIONNAIRE");

  useEffect(() => {
    let cancelled = false;
    api
      .get("/expenses/feature-status")
      .then((r) => { if (!cancelled) setUnlocked(!!r.data?.unlocked); })
      .catch(() => { if (!cancelled) setUnlocked(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!unlocked) return;
    setLoading(true);
    api
      .get(`/expenses/stats/dashboard?period=${period}&scope=${scope}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period, scope, unlocked]);

  // Hide the widget entirely for users without the Expense Notes feature.
  if (unlocked === false) return null;

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="dashboard-expense-stats">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ScanLine size={16} className="text-zinc-700" />
          <h3 className="font-heading text-sm font-bold tracking-tight">Dépenses par catégorie</h3>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {isAdminReader && (
            <div className="flex border border-zinc-200 rounded overflow-hidden mr-1">
              <button
                type="button"
                onClick={() => setScope("me")}
                data-testid="exp-stats-scope-me"
                className={`px-2 h-7 text-[10px] uppercase font-mono ${scope === "me" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
              >
                Moi
              </button>
              <button
                type="button"
                onClick={() => setScope("global")}
                data-testid="exp-stats-scope-global"
                className={`px-2 h-7 text-[10px] uppercase font-mono ${scope === "global" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
              >
                Tous
              </button>
            </div>
          )}
          <div className="flex border border-zinc-200 rounded overflow-hidden">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setPeriod(p.value)}
                data-testid={`exp-stats-period-${p.value}`}
                className={`px-2 h-7 text-[10px] uppercase font-mono ${period === p.value ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-xs text-zinc-500 flex items-center gap-2 py-6 justify-center">
          <Loader2 size={12} className="animate-spin" /> Chargement…
        </div>
      )}

      {!loading && data && (
        <div className="space-y-4">
          <div data-testid="exp-stats-ndf-section">
            <div className="flex items-center justify-between text-[11px] mb-2">
              <span className="font-mono uppercase tracking-[0.18em] text-zinc-500">
                Notes de frais ({scope === "me" ? "personnel" : "tous collab."})
              </span>
              <span className="font-mono text-zinc-700">
                <b>{data.ndf?.count || 0}</b> ligne(s) ·
                Total <b className="text-zinc-950">{fmt(data.ndf?.total_mad)}</b> MAD
              </span>
            </div>
            <CategoryBars rows={data.ndf?.by_category || []} />
          </div>

          {isAdminReader && data.cabinet?.available && (
            <div className="border-t border-zinc-100 pt-4" data-testid="exp-stats-cabinet-section">
              <div className="flex items-center justify-between text-[11px] mb-2">
                <span className="font-mono uppercase tracking-[0.18em] text-zinc-500 inline-flex items-center gap-1">
                  <Wallet size={11} /> Dépenses cabinet
                </span>
                <span className="font-mono text-zinc-700">
                  <b>{data.cabinet?.count || 0}</b> ligne(s) ·
                  Total <b className="text-zinc-950">{fmt(data.cabinet?.total_mad)}</b> MAD
                </span>
              </div>
              <CategoryBars rows={data.cabinet?.by_category || []} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
